import './syslog-server-configuration.module.css';

import { useState, useEffect, useCallback } from "react";
import { ButtonGroup } from "react-bootstrap";
import { useDispatch } from "react-redux";
import { useTranslation } from "react-i18next";
import { cloneDeep } from "lodash";

// Hook
import useStickyHeader from "hooks/useStickyHeader";
import usePageContext from "hooks/usePageContext";
import useTable from "hooks/useTable";
import useMessage from "hooks/useMessage";

// Slice
import { uiActions } from "stores/slice/ui";

// Const
import { defaultPathList, tableColumn, defaultModalStatus } from "./const";

// Component
import Button from 'components/Button';
import Breadcrumbs from 'components/Breadcrumb';
import MessageBoxGroup from 'components/MessageBoxGroup';

import SyslogServerTable from './components/SyslogServerTable';
import SyslogServerPagination from './components/SyslogServerPagination';
import AddOrEditSyslogServerModal from './modals/AddOrEditSyslogServerModal';
import DeleteModal from './modals/DeleteModal';

// Controller
import controller from "./controller";

const SyslogServerConfiguration = () => {
  const dispatch = useDispatch();
  const { t } = useTranslation();

  // State
  const [modalStatus, setModalStatus] = useState({ ...defaultModalStatus });
  const [originSyslogServerList, setOriginSyslogServerList] = useState([]);
  const [currentSyslogServerList, setCurrentSyslogServerList] = useState([]);
  const [remainingSiteList, setRemainingSiteList] = useState([]);
  const [selectedSyslogServer, setSelectedSyslogServer] = useState(null);
  const [deletedSyslogServerId, setDeletedSyslogServerId] = useState(null);

  // Hook
  const { tableRef, stickyStyle } = useStickyHeader();
  const { isOrgLevel, selectedOrg } = usePageContext();
  const {
    messages, messagesType,
    commonErrorCode, resetMessages, setErrorMessage
  } = useMessage();
  const {
    selectedPage, selectedPageLimit,
    tableData, getTableHeader,
    changePage, changePageLimit, getCurrentPageData
  } = useTable(originSyslogServerList, tableColumn);
  const { getManagedSiteList } = usePageContext();

  // Method
  const init = useCallback(async () => {
    resetMessages();

    if (!isOrgLevel) {
      setErrorMessage(commonErrorCode.forbidMspOrg);
      return;
    }

    try {
      dispatch(uiActions.showSpinner());

      const response = await controller.getSyslogServer(selectedOrg.id);
      if (response.error) {
        throw response.error;
      }

      const syslogServerList = controller.parseRawData(response.data, selectedOrg);
      setOriginSyslogServerList(syslogServerList);
      setCurrentSyslogServerList(getCurrentPageData());
      setRemainingSiteList(controller.getRemainingSiteList(getManagedSiteList(), syslogServerList));

      dispatch(uiActions.hideSpinner());
    } catch (error) {
      setErrorMessage(error.code);
      dispatch(uiActions.hideSpinner());
    }
  }, [selectedOrg]);

  const changeModalStatus = useCallback((type, value) => {
    const updatedModalStatus = cloneDeep(modalStatus);
    updatedModalStatus[type].status = value;
    setModalStatus(updatedModalStatus);
  }, [modalStatus]);

  // Side effect
  useEffect(() => {
    init();
  }, [init, selectedOrg]);

  useEffect(() => {
    setCurrentSyslogServerList(getCurrentPageData());
  }, [selectedPage, selectedPageLimit, tableData]);

  return (
    <>
      <Breadcrumbs pathList={defaultPathList} />

      <div className="layout-container layout-container--column layout-container--fluid">
        {!isOrgLevel &&
          (<MessageBoxGroup
            messages={messages}
            ErrorMessagesType={messagesType.error.client}
            onClose={resetMessages}
          />)
        }
        {isOrgLevel &&
          (<MessageBoxGroup
            messages={messages}
            ErrorMessagesType={messagesType.error.server}
            onClose={resetMessages}
          />)
        }

        {isOrgLevel && (
          <>
            <div className="d-flex justify-content-between mt-3 mb-2">
              <ButtonGroup>
                <Button
                  label="ec211f7c20"
                  className="btn-grey"
                  onClick={() => {
                    setSelectedSyslogServer(null);
                    changeModalStatus(modalStatus.addOrEdit.type, true);
                  }}
                />
                <Button
                  label="f2a6c498fb"
                  className="btn-grey"
                  disabled={!controller.getDeleteButtonState(currentSyslogServerList)}
                  onClick={() => {
                    setDeletedSyslogServerId(null);
                    changeModalStatus(modalStatus.delete.type, true);
                  }}
                />
              </ButtonGroup>
            </div>

            <SyslogServerTable
              tableRef={tableRef}
              stickyStyle={stickyStyle}
              tableHeader={getTableHeader}
              selectedPage={selectedPage}
              selectedPageLimit={selectedPageLimit}
              currentSyslogServerList={currentSyslogServerList}
              modalStatus={modalStatus}
              changeModalStatus={changeModalStatus}
              checkedAllState={() => controller.getCheckAllState(currentSyslogServerList)}
              toggleCheckedAll={() => setCurrentSyslogServerList(controller.toggleCheckedAll(currentSyslogServerList))}
              toggleChecked={index => setCurrentSyslogServerList(controller.toggleChecked(currentSyslogServerList, index))}
              selectSyslogServerCallback={syslogServer => setSelectedSyslogServer(syslogServer)}
              handleDelete={id => setDeletedSyslogServerId(id)}
            />

            <SyslogServerPagination
              originSyslogServerList={originSyslogServerList}
              selectedPage={selectedPage}
              setSelectedPage={changePage}
              setSelectedPageLimit={changePageLimit}
            />

            {/* Add or edit SMS account modal */}
            <AddOrEditSyslogServerModal
              modalStatus={modalStatus}
              changeModalStatus={changeModalStatus}
              selectedSyslogServer={selectedSyslogServer}
              managedSiteList={getManagedSiteList()}
              remainingSiteList={remainingSiteList}
              successCallback={init}
            />

            {/* Delete modal */}
            <DeleteModal
              modalStatus={modalStatus}
              selectedOrg={selectedOrg}
              sourceList={currentSyslogServerList}
              selectedId={deletedSyslogServerId}
              title={!deletedSyslogServerId ? '8b749aed89' : '7648be1230'}
              description="7e6f01943b"
              changeModalStatus={() => changeModalStatus(modalStatus.delete.type, false)}
              execute={(orgId, idList) => controller.deleteSyslogServer(orgId, idList)}
              successCallback={init}
            />
          </>
        )}
      </div>
    </>
  );
};

export default SyslogServerConfiguration;
